import Image from "next/image";
import { useState } from "react";
import axios from "axios";
import { useTranslation } from "@/contexts/TranslationContext";
import { Dancing_Script } from "next/font/google";
import ToastContainer from "@/components/ui/ToastContainer";
import BoxBlock from "@/components/ui/BoxBlock";
import {
    X,
    Mail,
    User,
    Loader2,
} from "lucide-react";

const ds = Dancing_Script({
    weight: ["400", "500", "600", "700"],
    subsets: ["latin"],
});

export default function HomeInfoSection() {
    const { t } = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEventTitle, setSelectedEventTitle] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [consentGiven, setConsentGiven] = useState(false);

    // ✅ Toast state + helpers (ugyanaz a minta mint nálad)
    const [toasts, setToasts] = useState([]);

    const [submitting, setSubmitting] = useState(false);

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    const pushToast = (type, title, message, opts = {}) => {
        const id = opts.id ?? `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const timeoutMs = opts.timeoutMs ?? 3000;

        setToasts((prev) => [...prev, { id, type, title, message }]);

        if (timeoutMs !== null) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, timeoutMs);
        }

        return id;
    };

    const handleBackendToasts = (json) => {
        if (Array.isArray(json?.toasts)) {
            for (const tObj of json.toasts) {
                pushToast(
                    tObj.type,
                    t(`toast.${tObj.type}`),
                    t(tObj.translationKey, tObj.params)
                );
            }
            return true; // volt backend toast
        } else if (json?.toast) {
            // legacy support
            pushToast(json.toast.type, json.toast.title, json.toast.message);
            return true;
        }
        return false;
    };

    const closeModal = () => setIsModalOpen(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!consentGiven) {
            pushToast(
                "error",
                t("toast.error"),
                t("newsletter.consent_required") ?? "Kérlek fogadd el az adatkezelést!"
            );
            return;
        }

        if (submitting) return;
        setSubmitting(true);

        const loadingToastId = pushToast(
            "info",
            t("toast.info"),
            t("newsletter.please_wait") ?? "Kis türelmet, feldolgozzuk a feliratkozást…",
            { timeoutMs: null } // <- ez marad, amíg nem töröljük
        );

        try {
            const res = await axios.post(`/api/newsletter-subscribers/subscribe`, {
                data: {
                    email,
                    name,
                    consentGiven,
                    category: selectedEventTitle || "Általános",
                },
            });

            removeToast(loadingToastId);

            const hadBackendToast = handleBackendToasts(res?.data);

            if (!hadBackendToast) {
                pushToast("success", t("toast.success"), t("newsletter.success") ?? "Sikeres feliratkozás!");
            }

            setEmail("");
            setName("");
            setConsentGiven(false);
            closeModal();
        } catch (error) {
            removeToast(loadingToastId);

            const backendJson = error?.response?.data;
            const hadBackendToast = handleBackendToasts(backendJson);

            if (!hadBackendToast) {
                pushToast("error", t("toast.error"), t("newsletter.error") ?? "Hiba történt a feliratkozás során.");
            }

            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <>
            {/* ✅ ToastContainer a section fölött, hogy a modal fölé is jöjjön */}
            <ToastContainer
                toasts={toasts}
                removeToast={removeToast}
            />

            <section className="bg-white py-10 px-4 sm:px-6 lg:px-8 font-sans">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-3xl md:text-5xl font-bold mb-8 text-black">
                        {t("home.info.section.title")}
                    </h1>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div className="text-left text-justify">
                            <p className="text-2xl font-light text-gray-700">
                                {t("home.info.section.paragraph")}
                            </p>
                        </div>

                        <div className="flex flex-col items-center">
                            <div className="w-60 h-60 relative mb-4">
                                <Image
                                    src="/themes/kulturklub/racz_laszlo_kulturklub_2_dd1fff3015.webp"
                                    alt="Rácz László"
                                    fill
                                    unoptimized
                                    className="rounded-full object-cover object-top"
                                />
                            </div>
                            <p className={`${ds.className} text-[#fe64db] text-[25px]`}>
                                {t("home.info.section.name")}
                            </p>
                            <p className="text-gray-600 text-sm">{t("home.info.section.somelier")}</p>
                        </div>
                    </div>

                    <div className="mt-10">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                            {t("home.info.section.join.title")}
                        </h2>

                        <button
                            onClick={() => {
                                setSelectedEventTitle("KultúrKlub");
                                setIsModalOpen(true);
                            }}
                            className="cursor-pointer mt-6 block mx-auto flex items-center justify-center px-6 py-3 text-white rounded hover:opacity-90 transform transition-transform duration-300 hover:scale-105 bg-[#fe64db] animate-pulse"
                        >
                            <span className="text-2xl font-bold mr-2">i</span>
                            {t("home.info.section.join.button")}
                        </button>
                    </div>
                </div>

                {isModalOpen && (
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
                        onClick={closeModal} // háttérkatt → bezár
                    >
                        <div
                            className="w-full md:max-w-lg"
                            onClick={(e) => e.stopPropagation()} // belső katt → ne zárjon
                        >
                            <BoxBlock
                                title={t("home.info.section.join.title2") ?? "Csatlakozz!"}
                                icon={<X />}
                                iconOnClick={closeModal}
                                className="divide-y divide-gray-100 dark:divide-zinc-700"
                            >
                                {/* Intro */}
                                <div className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                                    {t("home.info.section.modal.text") ??
                                        "Add meg az e-mail címed, és elsőként értesítünk az új programokról és akciókról!"}
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit} className="px-4 py-4 space-y-3">
                                    {/* Email */}
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {t("newsletter.email") ?? "Email cím"}
                                        </label>
                                        <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2">
                                            <Mail className="w-4 h-4 text-gray-400" />
                                            <input
                                                type="email"
                                                name="email"
                                                autoComplete="email"
                                                inputMode="email"
                                                placeholder="hello@..."
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="w-full bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
                                            />
                                        </div>
                                    </div>

                                    {/* Név */}
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {t("newsletter.name") ?? "Név (opcionális)"}
                                        </label>
                                        <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2">
                                            <User className="w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                name="name"
                                                autoComplete="name"
                                                placeholder={t("newsletter.name.placeholder") ?? "Pl. Bence"}
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                className="w-full bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
                                            />
                                        </div>
                                    </div>

                                    {/* Consent */}
                                    <div className="rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50/70 dark:bg-zinc-900/40 px-3 py-3">
                                        <label className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                id="consentGiven"
                                                checked={consentGiven}
                                                onChange={(e) => setConsentGiven(e.target.checked)}
                                                className="mt-1"
                                            />
                                            <span>
                                                Elfogadom az{" "}
                                                <a
                                                    href="/pages/adatkezelesi-tajekoztato"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="underline underline-offset-2"
                                                >
                                                    adatkezelési tájékoztatót
                                                </a>
                                                .
                                            </span>
                                        </label>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="w-full py-2 rounded-xl border border-gray-300 dark:border-zinc-700
                         bg-white dark:bg-zinc-900 text-gray-800 dark:text-gray-200
                         hover:bg-gray-50 dark:hover:bg-zinc-800 transition cursor-pointer"
                                        >
                                            Mégse
                                        </button>

                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className={`w-full py-2 rounded-xl font-semibold text-white transition cursor-pointer
    ${submitting ? "opacity-60 cursor-not-allowed bg-[#fe64db]" : "bg-[#fe64db] hover:opacity-90"}`}
                                        >
                                            {submitting && <Loader2 className="w-4 h-4 animate-spin inline-block mr-2 align-[-2px]" />}
                                            {submitting ? (t("newsletter.submitting") ?? "Feliratkozás…") : "Feliratkozás"}
                                        </button>
                                    </div>
                                </form>
                            </BoxBlock>
                        </div>
                    </div>
                )}

            </section>
        </>
    );
}
